// Extraído de AppLayout.jsx (refactor mecánico): mismos endpoints, frecuencias y condiciones por rol.
import { useEffect, useState } from "react";
import api from "@/lib/api";

const INITIAL = { leads_pendientes: 0, tareas_pendientes: 0, peticiones_ia_pendientes: 0, validaciones_pendientes: 0, fotos_pendientes: 0, reportes_pendientes: 0, diag_errores: 0 };

// Propuesta (no implementada, decisión de producto): un `GET /api/badges/resumen` que devuelva los 7 contadores
// en una sola llamada cada 20 s reduciría ~11 req/min → ~5 req/min por pestaña (cambia frecuencias).
let _reportesUltima = 0;
let _reportesEnVuelo = null;

export function useBadgesPolling(user, hasRole) {
  const [badges, setBadges] = useState(INITIAL);

  // Polling badges
  useEffect(() => {
    if (!hasRole("admin") && !hasRole("comercial")) return;
    let cancel = false;
    const fetchBadges = async () => {
      try {
        const { data } = await api.get("/leads/pendientes");
        if (!cancel) setBadges((b) => ({ ...b, leads_pendientes: data.count || 0 }));
      } catch { /* silent */ }
    };
    fetchBadges();
    const id = setInterval(fetchBadges, 60_000);
    return () => { cancel = true; clearInterval(id); };
  }, [hasRole, user?.id]);

  // Polling errores frontend (admin only) — alimenta el badge del menú "Diagnóstico"
  useEffect(() => {
    if (!hasRole("admin")) return;
    let cancel = false;
    const fetchDiag = async () => {
      try {
        const { data } = await api.get("/diag/frontend-errors/unread-count");
        if (!cancel) setBadges((b) => ({ ...b, diag_errores: data.count || 0 }));
      } catch { /* silent */ }
    };
    fetchDiag();
    const id = setInterval(fetchDiag, 60_000);
    return () => { cancel = true; clearInterval(id); };
  }, [hasRole, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    let cancel = false;
    const fetchTareas = async () => {
      try {
        const { data } = await api.get("/tareas/mias-pendientes");
        if (!cancel) setBadges((b) => ({ ...b, tareas_pendientes: data.count || 0 }));
      } catch { /* silent */ }
    };
    fetchTareas();
    const id = setInterval(fetchTareas, 60_000);
    return () => { cancel = true; clearInterval(id); };
  }, [user?.id]);

  // Polling badge Bandeja IA (sólo roles validadores)
  useEffect(() => {
    const roles = user?.roles || [];
    const isValidator = ["admin", "comercial", "produccion", "administracion"].some((r) => roles.includes(r));
    if (!isValidator) return;
    let cancel = false;
    const fetchIA = async () => {
      try {
        const { data } = await api.get("/peticiones-ia/count");
        if (!cancel) setBadges((b) => ({ ...b, peticiones_ia_pendientes: data.count || 0 }));
      } catch { /* silent */ }
    };
    fetchIA();
    const id = setInterval(fetchIA, 60_000);
    return () => { cancel = true; clearInterval(id); };
  }, [user?.id, user?.roles?.join(",")]);

  // Polling badge Validaciones pendientes (sólo admin)
  useEffect(() => {
    if (!hasRole("admin")) return;
    let cancel = false;
    const fetchVal = async () => {
      try {
        const { data } = await api.get("/validaciones/pendientes");
        if (!cancel) setBadges((b) => ({ ...b, validaciones_pendientes: data.count || 0 }));
      } catch { /* silent */ }
    };
    fetchVal();
    const id = setInterval(fetchVal, 45_000);
    return () => { cancel = true; clearInterval(id); };
  }, [hasRole, user?.id]);

  // Polling badge Fotos pendientes de aprobar para cliente
  useEffect(() => {
    const roles = user?.roles || [];
    const canValidate = ["admin", "gestoria", "administracion", "produccion", "logistica", "comercial", "diseño"].some((r) => roles.includes(r));
    if (!canValidate) return;
    let cancel = false;
    const fetchFotos = async () => {
      try {
        const { data } = await api.get("/fotos-pendientes-cliente?dias=21");
        if (!cancel) {
          const total = (data || []).reduce((acc, r) => acc + (r.n_pendientes || 0), 0);
          setBadges((b) => ({ ...b, fotos_pendientes: total }));
        }
      } catch { /* silent */ }
    };
    fetchFotos();
    const id = setInterval(fetchFotos, 60_000);
    return () => { cancel = true; clearInterval(id); };
  }, [user?.id, user?.roles?.join(",")]);

  // Polling badge Reportes pendientes (admin / oficina)
  // Iter 77 — refresh cada 20s + cross-tab vía storage event + visibility-aware.
  useEffect(() => {
    const roles = user?.roles || [];
    const canSee = ["admin", "produccion", "logistica", "comercial"].some((r) => roles.includes(r));
    if (!canSee) return undefined;
    let cancel = false;
    const fetchReportes = async () => {
      if (document.hidden) return;
      // Sep-2026 · dedupe entre montajes/efectos: 1 petición en vuelo y mínimo 5 s entre llamadas (endpoint pesado)
      const ahora = Date.now();
      if (!_reportesEnVuelo && ahora - _reportesUltima < 5_000) return;
      try {
        if (!_reportesEnVuelo) {
          _reportesUltima = ahora;
          _reportesEnVuelo = api.get("/reportes-pendientes", {
            params: { _t: ahora },
            headers: { "Cache-Control": "no-cache" },
          }).finally(() => { _reportesEnVuelo = null; });
        }
        const { data } = await _reportesEnVuelo;
        if (!cancel) {
          setBadges((b) => ({ ...b, reportes_pendientes: (data || []).length }));
        }
      } catch { /* silent */ }
    };
    fetchReportes();
    const id = setInterval(fetchReportes, 60_000);
    const visHandler = () => { if (!document.hidden) fetchReportes(); };
    const storageHandler = (e) => {
      if (e.key === "dnf-reporte-actualizado") fetchReportes();
    };
    document.addEventListener("visibilitychange", visHandler);
    window.addEventListener("storage", storageHandler);
    return () => {
      cancel = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", visHandler);
      window.removeEventListener("storage", storageHandler);
    };
  }, [user?.id, user?.roles?.join(",")]);

  return badges;
}
